import { useState } from "react";
import { Task, TaskComment, tasksApi } from "../../lib/api/tasks";
import { TaskEditModal } from "./TaskEditModal";

interface TaskDetailsProps {
  task: Task;
  onClose: () => void;
  onTaskUpdated: (updatedTask: Task) => void;
}

export function TaskDetails({
  task,
  onClose,
  onTaskUpdated,
}: TaskDetailsProps) {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const comment = await tasksApi.createComment(task._id, newComment.trim());
      setComments((prev) => [comment, ...prev]);
      setNewComment("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create comment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold mb-2">{task.title}</h2>
              <div className="flex gap-3 text-sm text-gray-500">
                <span>{task.type}</span>
                <span>·</span>
                <span
                  className={`px-2 py-0.5 rounded ${
                    task.priority === "URGENT"
                      ? "bg-red-100 text-red-800"
                      : task.priority === "HIGH"
                        ? "bg-orange-100 text-orange-800"
                        : task.priority === "MEDIUM"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                  }`}
                >
                  {task.priority}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowEditModal(true)}
                className="text-blue-500 hover:text-blue-600"
              >
                Edit
              </button>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Description
              </h3>
              <div className="text-gray-600 whitespace-pre-wrap">
                {task.description || "No description provided."}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-4">
                Comments
              </h3>
              <form onSubmit={handleSubmitComment} className="mb-6">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full px-4 py-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                />
                {error && (
                  <div className="text-red-600 text-sm mt-2">{error}</div>
                )}
                <div className="mt-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={loading || !newComment.trim()}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                  >
                    {loading ? "Adding..." : "Add Comment"}
                  </button>
                </div>
              </form>

              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment._id} className="border-b pb-4">
                    <div className="text-sm text-gray-600 mb-2">
                      {comment.content}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(comment.createdAt).toLocaleDateString(
                        undefined,
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEditModal && (
        <TaskEditModal
          task={task}
          onClose={() => setShowEditModal(false)}
          onTaskUpdated={(updatedTask) => {
            onTaskUpdated(updatedTask);
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
}
